import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Video, CheckCircle2, CreditCard, Lock } from "lucide-react";
import { OC_DATES, upcomingDates } from "@/components/oc/dates";

export function ExistingApplications({ applications }) {
  if (!applications?.length) return null;
  return (
    <div className="mb-6 space-y-2" data-testid="oc-existing">
      {applications.map((a) => (
        <div key={a.id} className="flex items-center justify-between rounded-lg bg-brand-light px-4 py-2 text-sm">
          <span className="text-brand font-medium">{a.registrant_name} {a.included && <span className="text-xs text-rag-green">(included)</span>}</span>
          <span className="text-xs capitalize text-muted-foreground">{a.status.replace("_", " ")}</span>
        </div>
      ))}
    </div>
  );
}

export function PriceNote({ included }) {
  return (
    <div className={`rounded-xl p-4 mb-6 text-sm ${included ? "bg-rag-green-bg text-rag-green" : "bg-coral/10 text-coral"}`} data-testid="oc-price-note">
      {included ? "This will use your care home's included Oral Champion place — no charge." : "You've used your included place. This additional champion is £42.50 (50% off)."}
    </div>
  );
}

export function TrialLocked({ onSubscribe }) {
  return (
    <div data-testid="oc-trial-locked" className="rounded-xl border border-coral/30 bg-coral/5 p-6">
      <div className="flex items-center gap-2 text-brand font-semibold"><Lock className="h-5 w-5 text-coral" /> Included with your paid subscription</div>
      <p className="text-sm text-muted-foreground mt-2">Oral Champion registration opens as soon as your subscription starts. Your first champion place is included free with every annual plan.</p>
      <Button data-testid="oc-subscribe-btn" onClick={onSubscribe} className="mt-4 rounded-full bg-coral hover:bg-coral-hover text-white">See plans & subscribe</Button>
    </div>
  );
}

export function ChampionConfirmed({ confirmed, onReset }) {
  return (
    <div data-testid="oc-confirmed" className="rounded-xl bg-rag-green-bg border border-rag-green/30 p-6 mb-6">
      <div className="flex items-center gap-2 text-rag-green font-semibold"><CheckCircle2 className="h-5 w-5" /> You're booked in!</div>
      <p className="text-sm text-foreground mt-2">We've emailed the Zoom webinar link to <b>{confirmed.email || "your inbox"}</b>{confirmed.session ? <> for <b>{confirmed.session}</b></> : null}. You'll also get a reminder before the session, and your certificate is issued once you've attended.</p>
      <div className="flex flex-wrap gap-3 mt-4">
        {confirmed.zoom_link && <a href={confirmed.zoom_link} target="_blank" rel="noreferrer"><Button data-testid="oc-join-zoom" className="rounded-full bg-brand text-white h-10"><Video className="h-4 w-4 mr-2" />Join the Zoom webinar</Button></a>}
        <a href={confirmed.info_url || "https://www.kohc.co.uk/oral-care-champions"} target="_blank" rel="noreferrer"><Button variant="outline" data-testid="oc-learn-more" className="rounded-full h-10">Learn more about the role</Button></a>
      </div>
      <button onClick={onReset} className="text-xs text-muted-foreground underline mt-4">Nominate another champion</button>
    </div>
  );
}

export function ChampionApplyForm({ form, setForm, included, loading, onSubmit }) {
  const field = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const pickDate = (v) => setForm({ ...form, session_date: v, session_at: (OC_DATES.find((d) => d.label === v) || {}).at || "" });
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <div><Label>Registrant's name</Label><Input data-testid="oc-name" required value={form.registrant_name} onChange={field("registrant_name")} /></div>
        <div><Label>Email</Label><Input data-testid="oc-email" type="email" required value={form.email} onChange={field("email")} /></div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div><Label>Job title</Label><Input data-testid="oc-job" value={form.job_title} onChange={field("job_title")} /></div>
        <div><Label>Type of care provided</Label><Input data-testid="oc-caretype" value={form.care_type} onChange={field("care_type")} /></div>
      </div>
      <div><Label>Preferred webinar date</Label>
        <Select value={form.session_date} onValueChange={pickDate}>
          <SelectTrigger data-testid="oc-date"><SelectValue placeholder="Select an available date" /></SelectTrigger>
          <SelectContent className="max-h-64">{upcomingDates().map((d) => <SelectItem key={d.label} value={d.label}>{d.label}</SelectItem>)}</SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground mt-1">From May 2027, sessions run the 2nd Tuesday of each month, 10am–1pm.</p>
      </div>
      <Button data-testid="oc-apply-btn" type="submit" disabled={loading} className="w-full h-11 rounded-full bg-coral hover:bg-coral-hover text-white">
        {included ? "Book my included champion" : <><CreditCard className="h-4 w-4 mr-2" />Pay £42.50 & register</>}
      </Button>
      <p className="text-xs text-muted-foreground text-center">You'll receive a confirmation and the Zoom link ahead of the course. <a href="https://www.kohc.co.uk/oral-care-champions" target="_blank" rel="noreferrer" className="text-brand underline">Learn more about the Oral Champion role</a>.</p>
    </form>
  );
}
